import * as Factory from 'factory.ts'
import faker from 'faker'

import {
  OrderItemStatuses,
  OrderItemTypes,
  ORDER_ITEM_DURATION_UNITS,
  ORDER_ITEM_FREQUENCY_UNITS,
  PunchOutStatuses,
} from '@webapp/models/enums'
import { type OrderItem } from '@webapp/models/OrderItem'
import { type OrderItemRecurrence } from '@webapp/models/OrderItemRecurrence'
import { AccountMock } from './account.factory'
import { CurrencyMock } from './currency.factory'
import { CustomFieldValueMock } from './customfield.factory'
import { VendorMock } from './vendor.factory'

export const OneTimeOrderItemMock = Factory.Sync.makeFactory<OrderItem>({
  account: AccountMock.build(),
  approved_price: faker.commerce.price(),
  currency: CurrencyMock.build({ name: 'CAD' }),
  custom_fields: CustomFieldValueMock.buildList(4),
  id: Factory.each(() => faker.random.number()),
  price: faker.commerce.price(),
  name: Factory.each(() => faker.commerce.productName()),
  pref_vendor: Factory.each(() => VendorMock.build()),
  quantity: Factory.each(() => String(faker.random.number())),
  unit: faker.commerce.productAdjective(),
  recurrence: null,
  sku: Factory.each(() => faker.random.alphaNumeric(6)),
  total_cost: Factory.each(() => faker.commerce.price().toString()),
  po_note: Factory.each(() => faker.lorem.paragraph()),
  PO_line_num: Factory.each(() => faker.random.number(5) + 1), // avoid 0
  lineComment: faker.lorem.sentence(),
  num: Factory.each(() => faker.random.number().toString()),
  orderNum: Factory.each(() => faker.random.number()),
  order_number: Factory.each(() => faker.random.number()),
  status: OrderItemStatuses.DRAFT,
  order_uuid: Factory.each(() => faker.random.uuid()),
  date_required: Factory.each(() => faker.date.future().toISOString()),
  vendor: Factory.each(() => faker.company.companyName()),
  tax_amount: null,
  approved_tax_amount: null,
  shipping_amount: null,
  approved_shipping_amount: null,
  type: OrderItemTypes.ONE_TIME,
  receive_method: null,
}).withDerivation1(
  ['approved_price'],
  'price',
  (approvedPrice) => approvedPrice
)

export const RecurringOrderItemRecurrenceMock =
  Factory.Sync.makeFactory<OrderItemRecurrence>({
    duration: ORDER_ITEM_DURATION_UNITS.INDEFINITE,
    duration_quantity: 1,
    frequency: ORDER_ITEM_FREQUENCY_UNITS.MONTHLY,
    periods_count: 12,
    recurring_price: '1000.00000000',
    start_date: faker.date.recent().toISOString().split('T')[0],
  })

export const RecurringOrderItemMock = Factory.Sync.makeFactory<OrderItem>({
  account: AccountMock.build(),
  currency: CurrencyMock.build({ name: 'CAD' }),
  id: Factory.each(() => faker.random.number()),
  price: '12000.00000000',
  quantity: '1.00000000',
  unit: 'Contract',
  name: faker.commerce.productName(),
  pref_vendor: Factory.each(() => VendorMock.build()),
  recurrence: RecurringOrderItemRecurrenceMock.build(),
  sku: Factory.each(() => faker.random.alphaNumeric(6)),
  total_cost: Factory.each(() => faker.commerce.price().toString()),
  num: Factory.each(() => faker.random.number().toString()),
  orderNum: Factory.each(() => faker.random.number()),
  order_number: Factory.each(() => faker.random.number()),
  order_uuid: Factory.each(() => faker.random.uuid()),
  PO_line_num: Factory.each(() => faker.random.number(5) + 1),
  date_required: Factory.each(() => faker.date.future().toISOString()),
  receive_method: null,
  type: OrderItemTypes.RECURRING,
})

export const PunchOutOrderItemMock = OneTimeOrderItemMock.extend({
  shipping_amount: Factory.each(() => String(faker.commerce.price())),
  tax_amount: Factory.each(() => String(faker.commerce.price())),
  punchout_uuid: faker.random.uuid(),
  punchout_system: 1,
  confirmation_status: PunchOutStatuses.ORDER_PLACED,
  punchout_vendor_reference: 4,
  punchout_supplier_name: Factory.each(
    () => `Item supplier ${faker.random.word()}`
  ),
  punchout_supplier_icon: Factory.each(() => faker.internet.url()),
})
